/* global AuEmbed, TradeInValuations, RenaultDaciaTracking, trackRenaultDacia, trackingConstants */
/* global XdMessaging */

import React, { Component } from "react"
import PropTypes from "prop-types"
import * as deepmerge from "deepmerge"

import CarAssemblage from "../CarAssemblage"
import TradeInFlowSelector from "../TradeInFlowSelector"
import BuyerIntentions from "../TradeInResult/BuyerIntentions"
import ProgressBar from "../../../car_condition_reports/ProgressBar"
import { CountryProvider } from "component_library/contexts/countryContext"
import { clickTrackingDataPayload } from "../Utility/clickTrackingData"
import { TradeInThemeProvider } from "./contexts/theme"
import { ErrorBoundary } from "react-error-boundary"
import errorStyles from "../CarAssemblage/Error.module.sass"

class TradeInWizard extends Component {
  static propTypes = {
    brandAndModelOptions: PropTypes.object.isRequired,
    carAssemblageInitialPayload: PropTypes.shape({
      seller_car_url: PropTypes.string.isRequired,
    }).isRequired,
    carAssemblageUrl: PropTypes.string.isRequired,
    conditionOptions: PropTypes.array,
    conditionAttributes: PropTypes.array,
    departmentOptions: PropTypes.array,
    departmentParamName: PropTypes.string,
    customerId: PropTypes.string,
    initialAssembledCar: PropTypes.object,
    initialBrandModelText: PropTypes.string,
    labels: PropTypes.object.isRequired,
    integrationType: PropTypes.string,
    fromModal: PropTypes.bool,
    trackFunction: PropTypes.func,
    theme: PropTypes.object.isRequired,
    kmPlaceholder: PropTypes.string,
    locale: PropTypes.string,
    initialTradeInFlowSelectorOptions: PropTypes.object,
    postcodesForTypeaheadUrl: PropTypes.string,
    // Testing ContinueFromEmail Prompt
    emailCollectionTranslations: PropTypes.object,
    requiredEmailAddressField: PropTypes.bool,
    enableReengagementFlow: PropTypes.bool,
    onlyCarDetailsStep: PropTypes.bool,
    hideFutureCarDetailsSteps: PropTypes.bool,
    dealerName: PropTypes.string,
    enableAuTracker: PropTypes.bool,
    country: PropTypes.string.isRequired,
    user: PropTypes.object,
    initialLeadFormFieldValues: PropTypes.object,
    previouslyCreatedLead: PropTypes.object,
  }

  state = {
    carAssemblageDone: !!this.props.initialTradeInFlowSelectorOptions,
    tradeInFlowSelectorOptions:
      this.props.initialTradeInFlowSelectorOptions ||
        this.props.initialLeadFormFieldValues
        ? { leadFormFieldValues: this.props.initialLeadFormFieldValues }
        : {},
    requestedClose: false,
    carAssemblageProgress: { completed: 1, total: 20 },
  }

  componentDidMount() {
    // Start listening to messages from iframe parent window
    this.messageHandler = XdMessaging.handleMessageWith(
      this.handleMessage.bind(this),
      /.*/
    )
    window.addEventListener("message", this.messageHandler)
    if (typeof trackRenaultDacia !== "undefined" && trackRenaultDacia) {
      RenaultDaciaTracking.track(
        "formOpen",
        trackRenaultDacia.brand,
        trackRenaultDacia.country
      )
    }
  }

  componentWillUnmount() {
    window.removeEventListener("message", this.messageHandler)
  }

  isReplacementCarStepDone = () => {
    const flowOptions = this.state.tradeInFlowSelectorOptions
    return (
      !flowOptions.replacementCarSearch.enabled ||
      flowOptions.replacementCarChosen
    )
  }

  // Whether it's ready to prompt for email when the user requested to close the modal
  readyToPromptForIntentions = () =>
    this.state.tradeInFlowSelectorOptions.buyerIntentions &&
    this.state.tradeInFlowSelectorOptions.leadSent &&
    !this.state.tradeInFlowSelectorOptions.intentionSent &&
    this.state.tradeInFlowSelectorOptions.tradeInResult.displayValuation

  // Whether it's currently displaying the prompt for email
  isPromptingForIntentions = () =>
    this.readyToPromptForIntentions() && this.state.requestedClose

  handleMessage = (data) => {
    switch (data.action) {
      case "TradeInCalculator.requestToCloseModal":
        XdMessaging.sendMessage(window.parent, {
          action: "TradeInCalculator.closeModalResponse",
          instanceId: data.instanceId,
          // Confirming that modal can close happens when it's
          // not ready to prompt for email or
          // another request to close was received before
          confirmClose:
            !this.readyToPromptForIntentions() || this.state.requestedClose,
        })

        if (
          this.props.trackFunction &&
          this.readyToPromptForIntentions() &&
          !this.state.requestedClose
        )
          this.props.trackFunction({
            entity: "valuation_result",
            element: "buyer intention prompt",
            action: "displayed",
            details: "when module closed",
            flow: "trade_in",
            type: "macro_proceeding",
          })

        this.setState({ requestedClose: true })
        break
    }
  }

  handleDismissEmailPrompt = () => {
    XdMessaging.sendMessage(window.parent, {
      action: "TradeInCalculator.closeModal",
    })
    if (this.props.trackFunction)
      this.props.trackFunction({
        entity: "car_form",
        element: "email prompt",
        action: "dismissed",
        type: "negative_interaction",
        flow: "trade_in",
      })
  }

  carAssemblageDone = (payload) => {
    // Make post request to #create.json
    let headers = new Headers({
      "Content-Type": "application/json",
    })

    const experiments = AuEmbed.AbTesting.getRunningExperiments()

    const payloadArray = [
      this.props.carAssemblageInitialPayload,
      clickTrackingDataPayload(this.props.customerId),
      payload,
      { exp: experiments },
    ]

    if (this.props.previouslyCreatedLead) {
      payloadArray.push(this.props.previouslyCreatedLead)
    }

    payload = deepmerge.all(payloadArray)

    fetch(this.props.carAssemblageUrl, {
      method: "POST",
      body: JSON.stringify(payload),
      headers,
    })
      .then((res) => {
        return res.json()
      })
      .then((data) => {
        this.setState((prevState) => ({
          carAssemblageDone: true,
          tradeInFlowSelectorOptions: {
            ...prevState.tradeInFlowSelectorOptions,
            ...data.tradeInFlowSelectorOptions,
          },
        }))
        this._triggerCarDetailsSent(data)
      })
    if (this.props.trackFunction)
      this.props.trackFunction({
        entity: "car_form",
        action: "submitted",
        details: [
          this.props.hideFutureCarDetailsSteps ? "with hidden next steps" : "",
          this.props.initialBrandModelText
            ? "with initial brand model text"
            : "",
        ]
          .filter(Boolean)
          .join(", "),
        flow: "trade_in",
        type: "micro_proceeding",
      })
  }

  _triggerCarDetailsSent = ({
    valuated,
    buyerCarValuation,
    buyerCarValuationInterval,
    buyerCarEstimatedMarketPrice,
    buyerCar,
    buyerCarConditionGrade,
    buyerCarColor,
    lpn,
    lpnData,
    tradeInValuationId,
    isPriceHidden,
    autouncleRatingIntervals,
  }) => {
    if (valuated) {
      TradeInValuations.valuated({
        buyerCarValuation,
        buyerCarEstimatedMarketPrice,
        buyerCar,
        valuationRefNo: tradeInValuationId,
        isPriceHidden,
      })
    } else {
      TradeInValuations.valuationFailed({
        message: "Valuation failed",
        buyerCar,
        valuationRefNo: tradeInValuationId,
      })
    }
    TradeInValuations.carDetailsSent({
      valuated,
      buyerCar,
      buyerCarEstimatedMarketPrice,
      buyerCarValuation,
      buyerCarValuationInterval,
      buyerCarConditionGrade,
      buyerCarColor,
      lpn,
      lpnData,
      refId: tradeInValuationId,
      isPriceHidden,
      autouncleRatingIntervals,
    })
  }

  updateFlowSelectorOptions = (options) => {
    this.setState((prevState) => ({
      tradeInFlowSelectorOptions: {
        ...prevState.tradeInFlowSelectorOptions,
        ...options,
      },
    }))
  }

  handleEditReplacementCar = () => {
    if (this.props.trackFunction)
      this.props.trackFunction({
        entity: "car_form",
        element: "edit replacement car",
        action: "clicked",
        flow: "trade_in",
        type: "micro_proceeding",
      })

    this.setState((prevState) => ({
      tradeInFlowSelectorOptions: {
        ...prevState.tradeInFlowSelectorOptions,
        replacementCarChosen: false,
        replacementCarSearch: {
          ...prevState.tradeInFlowSelectorOptions.replacementCarSearch,
          enabled: true,
        },
      },
    }))
  }

  handleSelectedCarChange = (selectedCar) => {
    this.setState((prevState) => ({
      tradeInFlowSelectorOptions: {
        ...prevState.tradeInFlowSelectorOptions,
        replacementCarSearch: {
          ...prevState.tradeInFlowSelectorOptions.replacementCarSearch,
          preselectedCarInfo: selectedCar,
        },
      },
    }))
  }

  handlePostCodeCaptured = (value) => {
    const postcodeValue = [
      "trade_in_valuation[extra_lead_qualification][postcode]",
      value,
    ]
    this.setState((prevState) => ({
      tradeInFlowSelectorOptions: {
        ...prevState.tradeInFlowSelectorOptions,
        leadFormFieldValues: [postcodeValue],
      },
    }))
  }

  handleBuyerEmailAddressCaptured = (value) => {
    const buyerEmailValue = ["trade_in_valuation[buyer_email]", value]
    this.setState((prevState) => {
      const leadFormFieldValues =
        prevState.tradeInFlowSelectorOptions.leadFormFieldValues || []

      return {
        tradeInFlowSelectorOptions: {
          leadFormFieldValues: [...leadFormFieldValues, buyerEmailValue],
        },
      }
    })
  }

  handleLeadFormFieldValuesCaptured = (values) => {
    this.setState((prevState) => ({
      tradeInFlowSelectorOptions: {
        ...prevState.tradeInFlowSelectorOptions,
        leadFormFieldValues: values,
      },
    }))
  }

  handleCarAssemblageProgress = (completed, total) => {
    // We start with some initial progress, so we add 1 to make sure
    // progress is visible
    this.setState({
      carAssemblageProgress: { completed: completed + 1, total: total + 1 },
    })
  }

  render() {
    const { labels, trackFunction, theme, hideFutureCarDetailsSteps } =
      this.props
    const { carAssemblageProgress } = this.state

    /**
     *
     * @param {import("react-error-boundary").FallbackProps} fallbackProps
     * @returns
     */
    function fallbackRender({ resetErrorBoundary }) {
      return (
        <div className={errorStyles.error}>
          {labels.errorMessage}
          <br />
          <button onClick={resetErrorBoundary}>{labels.errorTryAgain}</button>
        </div>
      );
    }

    return (
      <TradeInThemeProvider value={theme}>
        <CountryProvider value={this.props.country}>
          {this.isPromptingForIntentions() ? (
            <BuyerIntentions
              {...this.state.tradeInFlowSelectorOptions.buyerIntentions}
              isFromClosing
              trackFunction={trackFunction}
              source="prompt"
              sendPicturesSmsProps={
                this.state.tradeInFlowSelectorOptions.sendPicturesSmsProps
              }
            />
          ) : !this.state.carAssemblageDone || this.props.onlyCarDetailsStep ? (
            <ErrorBoundary onError={(error, info) => {
              trackFunction({
                entity: "car_form",
                action: "displayed",
                type: "system_exception",
                details: `${error.name}: ${error.message}\n${info.componentStack}`,
              })
              Honeybadger.notify(
                new Error(`Error in CarAssemblage: ${error}: ${info.componentStack}`)
              )
            }} fallbackRender={fallbackRender}>
              <CarAssemblage
                brandAndModelOptions={this.props.brandAndModelOptions}
                finishedCallback={this.carAssemblageDone}
                initialAssembledCar={this.props.initialAssembledCar}
                initialBrandModelText={this.props.initialBrandModelText}
                labels={labels}
                trackFunction={trackFunction}
                conditionOptions={this.props.conditionOptions}
                conditionAttributes={this.props.conditionAttributes}
                departmentOptions={this.props.departmentOptions}
                departmentParamName={this.props.departmentParamName}
                scrollStepsIntoView={this.props.fromModal}
                autoFocusBrandModel={this.props.fromModal}
                theme={theme}
                kmPlaceholder={this.props.kmPlaceholder}
                locale={this.props.locale}
                postcodesForTypeaheadUrl={this.props.postcodesForTypeaheadUrl}
                onCapturePostCode={this.handlePostCodeCaptured}
                onCaptureBuyerEmailAddress={
                  this.handleBuyerEmailAddressCaptured
                }
                emailAddressTranslations={
                  this.props.emailCollectionTranslations
                }
                user={this.props.user}
                requiredEmailAddressField={this.props.requiredEmailAddressField}
                enableReengagementFlow={this.props.enableReengagementFlow}
                hideFutureSteps={hideFutureCarDetailsSteps}
                onProgress={this.handleCarAssemblageProgress}
              />
              {hideFutureCarDetailsSteps && (
                // Display a progress bar when we hide the next steps
                <div style={{ marginTop: "12px" }}>
                  <ProgressBar
                    completedSteps={carAssemblageProgress.completed}
                    steps={carAssemblageProgress.total}
                    color={theme.color}
                  />
                </div>
              )}
            </ErrorBoundary>
          ) : (
            <TradeInFlowSelector
              {...{
                ...this.state.tradeInFlowSelectorOptions,
                replacementCarSearch: {
                  ...this.state.tradeInFlowSelectorOptions.replacementCarSearch,
                  onSelectedCarChange: this.handleSelectedCarChange,
                },
              }}
              handleEditReplacementCar={this.handleEditReplacementCar}
              onLeadFormFieldValuesCaptured={
                this.handleLeadFormFieldValuesCaptured
              }
            />
          )}
        </CountryProvider>
      </TradeInThemeProvider>
    )
  }
}

export default TradeInWizard
